import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Send, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MandalaCorner } from '../components/luxury/MandalaCorner';
import { api } from '../App';
import { toast } from 'sonner';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const VOLUNTEER_AREAS = [
  'Event Organization & Planning',
  'Cultural Programs & Performances',
  'Youth & Education Activities',
  'Fundraising & Sponsorship',
  'Marketing & Social Media',
  'Technical Support (Website, IT)',
  'Administration & Office Support',
  'Other (please specify)',
];

export default function LuxuryVolunteer() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    areas: [],
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleAreaToggle = (area) => {
    setForm(prev => ({
      ...prev,
      areas: prev.areas.includes(area)
        ? prev.areas.filter(a => a !== area)
        : [...prev.areas, area]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.areas.length === 0) {
      toast.error('Please complete the required fields so we can get in touch');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/volunteer', {
        name: form.name,
        email: form.email,
        phone: form.phone,
        interests: form.areas,
        message: form.message,
      });
      toast.success('Application sent! We\'ll contact you soon.');
      setSubmitted(true);
    } catch (err) {
      toast.error(err.message || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-[hsl(var(--lux-royal-soft))] to-[hsl(var(--lux-ivory))] overflow-hidden">
        <MandalaCorner position="top-left" className="opacity-20" />
        <MandalaCorner position="bottom-right" className="opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Heart className="w-16 h-16 text-[hsl(var(--lux-gold))] mx-auto mb-6" fill="currentColor" />
            <h1 className="text-5xl sm:text-6xl font-bold text-[hsl(var(--lux-royal))] mb-6" style={{ fontFamily: 'Cormorant Garamond' }}>
              Volunteer <span className="text-[hsl(var(--lux-gold))]">With Us</span>
            </h1>
            <p className="text-lg text-[hsl(var(--lux-ink)/0.8)] max-w-2xl mx-auto mb-8">
              Join our dedicated team of volunteers and help strengthen our Gujarati community in Nottingham.
            </p>
            <div className="lux-gold-divider mx-auto max-w-md" />
          </motion.div>
        </div>
      </section>

      {/* Why Volunteer */}
      <section className="py-16 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl font-bold text-[hsl(var(--lux-royal))] mb-6 text-center" style={{ fontFamily: 'Cormorant Garamond' }}>
              Why Volunteer?
            </h2>
            <div className="grid sm:grid-cols-3 gap-6 mb-12">
              {[
                { title: 'Give Back', desc: 'Contribute to your community and make a lasting impact' },
                { title: 'Connect', desc: 'Meet like-minded people and build meaningful relationships' },
                { title: 'Grow', desc: 'Develop new skills and gain valuable experience' },
              ].map((item, idx) => (
                <motion.div key={idx} {...fadeInUp} transition={{ delay: idx * 0.1 }}>
                  <Card className="lux-ornate-card text-center h-full">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>
                        {item.title}
                      </h3>
                      <p className="text-sm text-[hsl(var(--lux-ink)/0.7)]">{item.desc}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
            <p className="text-center text-[hsl(var(--lux-ink)/0.8)] leading-relaxed">
              Gujarat Samaj Nottingham relies on the generous contributions of time and talent from our volunteers. Whether you can help with events, cultural programs, administration, or technical support, your involvement makes a difference.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Volunteer Form */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Card className="lux-ornate-card">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-[hsl(var(--lux-royal))] mb-8 text-center" style={{ fontFamily: 'Cormorant Garamond' }}>
                  Volunteer Application
                </h2>
                
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[hsl(var(--lux-gold-soft))] flex items-center justify-center">
                      <Check className="w-10 h-10 text-[hsl(var(--lux-gold))]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>
                      Thank You!
                    </h3>
                    <p className="text-[hsl(var(--lux-ink)/0.7)] mb-6">
                      Your volunteer application has been received. We'll be in touch soon with opportunities.
                    </p>
                    <Button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', areas: [], message: '' }); }} variant="outline" className="border-[hsl(var(--lux-gold)/0.5)]">
                      Submit Another Application
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Name *</Label>
                        <Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Your name" />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="your@email.com" />
                      </div>
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+44 123 456 7890" />
                    </div>
                    <div>
                      <Label className="mb-3 block">Areas of Interest *</Label>
                      <div className="space-y-2">
                        {VOLUNTEER_AREAS.map(area => (
                          <label key={area} className="flex items-center gap-3 p-3 border border-[hsl(var(--lux-gold)/0.3)] rounded-lg hover:bg-[hsl(var(--lux-royal-soft))] transition-colors cursor-pointer">
                            <input
                              type="checkbox"
                              checked={form.areas.includes(area)}
                              onChange={() => handleAreaToggle(area)}
                              className="w-4 h-4 text-[hsl(var(--lux-royal))]" />
                            <span className="text-sm text-[hsl(var(--lux-ink))]">{area}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Additional Information</Label>
                      <Textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Tell us about your skills, availability, or any questions..." rows={4} />
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))] h-12 text-lg">
                      <Send className="w-5 h-5 mr-2" />
                      {loading ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
